import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { CasinoReduxCommandCreator } from 'appRedux/actions/casinoCommandCreator';
import { IState } from 'appRedux/createStore';
import { selectAuthStateTransformed } from 'appRedux/selectors';
import {
  clearApplicationConfigurationsAll,
  getApplicationConfigLoginEpoch,
  isApplicationConfigSessionLoadComplete,
  isApplicationConfigSessionNetworkInFlight,
  markApplicationConfigSessionLoadComplete,
  markApplicationConfigSessionNetworkInFlight,
  planApplicationConfigLoad,
  subscribeApplicationConfigLoginEpoch,
  writeSessionApplicationConfigurations,
  type ApplicationConfigRow,
} from 'lib/applicationConfig';
import { CasinoService } from 'services/casino';

/**
 * After login: fetch GetApplicationConfigurations once, keep for the login
 * session (Redux + sessionStorage). Reload within the session hydrates from
 * sessionStorage with no network. Cleared on logout; next login refetches.
 * No TTL-based refetch. Fetch failure → empty set → getConfig falls back (months1).
 */
export function useLoadApplicationConfigurations(): void {
  const dispatch = useDispatch();
  const authState = useSelector(selectAuthStateTransformed);
  // Re-run after beginApplicationConfigLoginSession (auth may already be true).
  const [loginEpoch, setLoginEpoch] = useState(getApplicationConfigLoginEpoch);
  useEffect(() => subscribeApplicationConfigLoginEpoch(() => setLoginEpoch(getApplicationConfigLoginEpoch())), []);

  useEffect(() => {
    if (!authState) {
      clearApplicationConfigurationsAll();
      return;
    }
    if (isApplicationConfigSessionLoadComplete()) return;
    if (isApplicationConfigSessionNetworkInFlight()) return;

    const casinoCommands = CasinoReduxCommandCreator(dispatch);
    const plan = planApplicationConfigLoad();

    if (plan.action === 'use_session') {
      casinoCommands.SetApplicationConfigurations(plan.rows);
      markApplicationConfigSessionLoadComplete();
      return;
    }

    markApplicationConfigSessionNetworkInFlight(true);
    const svc = new CasinoService();
    const sub = svc.GetApplicationConfigurations().subscribe({
      next(resp) {
        const rows = (resp && resp.DataSet ? resp.DataSet : []) as ApplicationConfigRow[];
        casinoCommands.SetApplicationConfigurations(rows);
        writeSessionApplicationConfigurations(rows);
        markApplicationConfigSessionLoadComplete();
      },
      error() {
        // Empty → getConfig / egmDetailGraphDefaultTimeframe fall back to months1.
        casinoCommands.SetApplicationConfigurations([]);
        writeSessionApplicationConfigurations([]);
        markApplicationConfigSessionLoadComplete();
      },
    });
    return () => {
      sub.unsubscribe();
      if (!isApplicationConfigSessionLoadComplete()) {
        markApplicationConfigSessionNetworkInFlight(false);
      }
    };
  }, [authState, dispatch, loginEpoch]);
}

export function useApplicationConfigurations(): ApplicationConfigRow[] {
  return (
    (useSelector<IState>((s) => s.app.applicationConfigurations) as ApplicationConfigRow[] | undefined) ||
    []
  );
}
